import { post } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import {
  type InviteWorkerResponse,
  inviteWorkerRequestSchema,
  inviteWorkerResponseSchema,
} from "@src/appV2/ExperimentalHomeHealth/Cases/types";
import { useHomeHealthSurface } from "@src/appV2/ExperimentalHomeHealth/lib/useHomeHealthSurface";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useMutation, type UseMutationOptions, useQueryClient } from "@tanstack/react-query";

interface InviteWorkerToCaseVisitParams {
  visitId: string;
  workerId: string;
}

/**
 * Invites a worker to a specific visit. Like `useInviteWorker` but takes
 * `workplaceId` explicitly so it works for an admin viewing a workplace.
 */
export function useInviteWorkerToCaseVisit(
  params: { workplaceId: string },
  options: UseMutationOptions<InviteWorkerResponse, unknown, InviteWorkerToCaseVisitParams> = {}
) {
  const { workplaceId } = params;
  const queryClient = useQueryClient();
  const renderedSurface = useHomeHealthSurface();

  return useMutation({
    mutationFn: async ({ visitId, workerId }: InviteWorkerToCaseVisitParams) => {
      const response = await post({
        url: `${environmentConfig.REACT_APP_HOME_HEALTH_API_URL}/v1/${workplaceId}/visits/${visitId}/invites`,
        requestSchema: inviteWorkerRequestSchema,
        responseSchema: inviteWorkerResponseSchema,
        data: { data: { type: "invite", attributes: { visitId, workerId } } },
      });

      return response.data;
    },
    onSuccess: (response) => {
      logEvent(APP_V2_USER_EVENTS.HOME_HEALTH_VISIT_WORKER_INVITED, {
        workplaceId,
        visitId: response.relationships.visit.id,
        workerId: response.data.attributes.workerId,
        performedByAdmin: true,
        renderedSurface,
      });
      // Invalidate this workplace's cases queries (incl. the OPEN-filtered
      // `useAgencyCases` the invite picker reads) so the invited visit refreshes.
      void queryClient.invalidateQueries({ queryKey: ["home-health", workplaceId, "cases"] });
    },
    ...options,
  });
}
